import React from 'react';
import InputBox from './input-box';
import { ListResponse } from '../api/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

export type OptionType = { value: string, label: string };

interface Single {
    data?: Record<string, any>;
    value?: string;
    defaultValue?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    name?: string;
    label?: string;
    className?: string;
    wrapperClass?: string;
    loadWithData?: boolean;
    load?: (data?: Record<string, any>) => Promise<Array<OptionType>>;
    options?: Array<OptionType>;
    children?: React.ReactNode;
    comment?: string;
    onChange?: (value: string) => void;
}

interface Multiple {
    data?: Record<string, any>;
    value?: Array<string>;
    defaultValue?: Array<string>;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    name?: string;
    label?: string;
    className?: string;
    wrapperClass?: string;
    loadWithData?: boolean;
    load?: (data?: Record<string, any>) => Promise<Array<OptionType>>;
    options?: Array<OptionType>;
    children?: React.ReactNode;
    comment?: string;
    onChange?: (value: Array<string>) => void;
}

export function response2options(id: string, value: string, api: () => Promise<string | ListResponse<any>>): Promise<Array<OptionType>> {
    return new Promise(resolve => {
        api().then(response => {
            if (typeof response === 'string') {
                resolve([]);
                return;
            }
            let options: Array<OptionType> = [];
            for (let item of response.data) {
                options.push({
                    value: item[id],
                    label: item[value]
                });
            }
            resolve(options);
        }, () => resolve([]));
    });
}

export default React.forwardRef<HTMLSelectElement, Single>(function Select(props, ref) {
    const { 0: value, 1: setValue } = React.useState<string>(props.defaultValue || '');
    const { 0: options, 1: setOptions } = React.useState<Array<OptionType>>([]);
    const debounce = React.useRef(false);
    const loaded = React.useRef(false);
    const filteredProps = React.useMemo(() => {
        const tmp: Pick<Single, 'required' | 'name' | 'disabled' | 'readOnly'> = {};
        for (let i of ['required', 'name', 'disabled', 'readOnly']) {
            if (typeof props[(i as 'name')] !== 'undefined') {
                tmp[i as 'name'] = props[(i as 'name')];
            }
        }
        return tmp;
    }, [props]);
    React.useEffect(() => {
        if (props.load && (!loaded.current || props.loadWithData)) {
            if (debounce.current) {
                return;
            }
            debounce.current = true;
            props.load(props.data).then(options => {
                setOptions(options);
                debounce.current = false;
                loaded.current = true;
            });
        }
    }, [props.load, props.data, props.loadWithData, setOptions]);
    React.useEffect(() => {
        if (props.options) {
            setOptions(props.options);
        }
    }, [props.options, setOptions]);
    React.useEffect(() => {
        setValue(props.value ?? '');
    }, [props.value, setValue]);
    const onChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        (props.onChange || setValue)(e.target.value);
    }, [props.onChange, setValue]);
    return (
        <InputBox label={props.label} required={props.required || false} className={props.wrapperClass} comment={props.comment}>
            <>
                <select
                    className={props.className ? "form-control active " + props.className : "form-control active"}
                    {...filteredProps}
                    value={value}
                    onChange={onChange}
                    ref={ref}>
                    {props.children}
                    {options.map((option, index) => (
                        <option value={option.value} key={filteredProps.name + '-' + option.value + '-' + index}>{option.label}</option>
                    ))}
                </select>
                <FontAwesomeIcon icon={faCaretDown} />
            </>
        </InputBox>
    );
});

export function MultiSelect(props: Multiple) {
    const { 0: show, 1: setShow } = React.useState(false);
    const { 0: value, 1: setValue } = React.useState<Array<string>>(props.defaultValue || []);
    const { 0: options, 1: setOptions } = React.useState<Array<OptionType>>([]);
    const debounce = React.useRef(false);
    const loaded = React.useRef(false);
    React.useEffect(() => {
        if (props.load && (!loaded.current || props.loadWithData)) {
            if (debounce.current) {
                return;
            }
            debounce.current = true;
            props.load(props.data).then(options => {
                setOptions(options);
                debounce.current = false;
                loaded.current = true;
            });
        }
    }, [props.load, props.data, props.loadWithData, setOptions]);
    React.useEffect(() => {
        if (props.options) {
            setOptions(props.options);
        }
    }, [props.options, setOptions]);
    React.useEffect(() => {
        setValue(props.value ?? []);
    }, [props.value, setValue]);
    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const cloned = [...value];
        if (e.target.checked) {
            cloned.push(e.target.value);
        } else {
            const p = cloned.indexOf(e.target.value);
            p > -1 && cloned.splice(p, 1);
        }
        (props.onChange || setValue)(cloned);
    }, [value, props.onChange]);
    const box = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (show) {
            const l = (e: MouseEvent) => {
                let p: HTMLElement | null = e.target as HTMLElement;
                while (p) {
                    if (box.current && box.current === p || !box.current && p.classList.contains('options')) {
                        return;
                    }
                    p = p.parentElement;
                }
                setShow(false);
            };
            document.addEventListener('click', l);
            return () => document.removeEventListener('click', l);
        }
    }, [show, setShow]);
    return (
        <InputBox label={props.label} required={props.required || false} className={props.wrapperClass} comment={props.comment} ref={box}>
            <button disabled={props.disabled} type="button" onClick={setShow.bind(null, !show)}
                className={props.className ? "form-control active " + props.className : "form-control active"}>
                <span>{value.length ? value.join(',') : props.children} &nbsp;</span>
                <FontAwesomeIcon icon={faCaretDown} />
            </button>
            <div className={'options' + (show ? ' active' : '')}>
                {options.map((option, i) => (
                    <label key={option.value + i}>
                        <input type="checkbox" name={props.name} required={props.required} disabled={props.disabled} readOnly={props.readOnly}
                            value={option.value} checked={value.includes(option.value)} onChange={onChange} />
                        <span>{option.label}</span>
                    </label>
                ))}
            </div>
        </InputBox>
    );
}